import { useEffect, useState } from "react";
import { fetchFeatures, fetchSchedule, saveSchedule } from "../api/client";
import { Alert } from "./Alert";
import { Spinner } from "./Spinner";
import { useAsyncData } from "../hooks/useAsyncData";
import { formatError } from "../utils/formatError";
import type { RunMode, ScheduleReport, SourceInfo } from "../api/types";

/** 0 = Monday … 6 = Sunday, matching the server's `datetime.weekday()` numbering. */
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const MODES: RunMode[] = ["incremental", "full", "summarize"];

function formatHour(hour: number): string {
  return `${String(hour).padStart(2, "0")}:00`;
}

/** "02:00" or "02:30" → 2. The server stores whole hours, so anything past the colon is dropped. */
function parseHour(value: string): number | null {
  const hour = Number.parseInt(value.slice(0, 2), 10);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : null;
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

/** Add or remove one value, keeping the list sorted so it matches what the server stores. */
function toggled(values: number[], value: number): number[] {
  if (values.includes(value)) {
    return values.filter((existing) => existing !== value);
  }
  return [...values, value].sort((left, right) => left - right);
}

const ascending = (left: number, right: number): number => left - right;

interface ScheduleCardProps {
  /** The registered sources, already loaded by the Runs screen — not fetched a second time here. */
  sources: SourceInfo[];
}

/**
 * The indexing schedule: whether the indexer runs by itself, and on which days and at which hours.
 *
 * Whole hours only: the time picker is stepped to the hour and minutes are dropped, because that is
 * what the server stores. Saving never starts a run — the next matching hour does, within a minute
 * of the schedule being saved.
 */
export function ScheduleCard({ sources }: ScheduleCardProps): JSX.Element {
  const { data, loading, error, reload } = useAsyncData(
    () => Promise.all([fetchSchedule(), fetchFeatures()]),
    [],
  );
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState<number[]>([]);
  const [hours, setHours] = useState<number[]>([]);
  const [pickedTime, setPickedTime] = useState("02:00");
  const [timezone, setTimezone] = useState("UTC");
  const [mode, setMode] = useState<RunMode>("incremental");
  const [source, setSource] = useState("");
  const [report, setReport] = useState<ScheduleReport | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed the editable state whenever a fresh copy loads from the server.
  useEffect(() => {
    const loaded = data?.[0];
    if (loaded) {
      setEnabled(loaded.schedule.enabled);
      setDays(loaded.schedule.days);
      setHours(loaded.schedule.hours);
      setTimezone(loaded.schedule.timezone);
      setMode(loaded.schedule.mode);
      setSource(loaded.schedule.source);
      setReport(loaded);
    }
  }, [data]);

  // Checked here as well as on the server so the admin is told before the round trip, not by a 422.
  const problem =
    enabled && (days.length === 0 || hours.length === 0)
      ? "A schedule that is on needs at least one day and at least one hour, or it will never run."
      : null;

  const pickedHour = parseHour(pickedTime);

  const handleAddHour = (): void => {
    if (pickedHour !== null && !hours.includes(pickedHour)) {
      setHours([...hours, pickedHour].sort(ascending));
    }
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      setReport(
        await saveSchedule({
          enabled,
          days,
          hours,
          timezone,
          mode,
          source,
          updated_by: "",
          updated_at: null,
        }),
      );
    } catch (saveErr) {
      setSaveError(formatError(saveErr));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner label="Loading schedule…" />;
  }

  if (error) {
    return (
      <>
        <Alert variant="error">Could not load the schedule: {formatError(error)}</Alert>
        <button type="button" className="button button--secondary button--small" onClick={reload}>
          Retry
        </button>
      </>
    );
  }

  return (
    <div className="schedule-card">
      {data?.[1].schedule_enabled === false && (
        <Alert variant="warning">
          This deployment has the scheduler switched off, so no schedule saved here will run. Set
          INDEX_SCHEDULE_ENABLED and restart to turn it on.
        </Alert>
      )}

      <label className="toggle-row">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => setEnabled(event.target.checked)}
        />
        Run the indexer on a schedule
      </label>

      <fieldset className="checkbox-grid">
        <legend>Days</legend>
        {DAYS.map((label, day) => (
          <label key={label}>
            <input
              type="checkbox"
              checked={days.includes(day)}
              onChange={() => setDays(toggled(days, day))}
            />
            {label}
          </label>
        ))}
      </fieldset>

      <fieldset className="time-picker">
        <legend>Start times</legend>
        <div className="time-picker__add">
          <input
            type="time"
            step={3600}
            value={pickedTime}
            onChange={(event) => setPickedTime(event.target.value)}
            aria-label="Start time to add"
          />
          <button
            type="button"
            className="button button--secondary button--small"
            onClick={handleAddHour}
            disabled={pickedHour === null || hours.includes(pickedHour)}
          >
            Add time
          </button>
        </div>
        <ul className="time-picker__list">
          {hours.length === 0 && <li className="time-picker__empty">No start times yet.</li>}
          {hours.map((hour) => (
            <li key={hour} className="time-chip">
              {formatHour(hour)}
              <button
                type="button"
                className="time-chip__remove"
                onClick={() => setHours(hours.filter((existing) => existing !== hour))}
                aria-label={`Remove ${formatHour(hour)}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      </fieldset>

      <label className="field-row">
        <span className="field-row__label">Timezone</span>
        <input
          type="text"
          value={timezone}
          onChange={(event) => setTimezone(event.target.value)}
          placeholder="Europe/London"
        />
        <span className="field-row__hint">
          An IANA name. The start times above are read in this zone, not the server’s.
        </span>
      </label>

      <div className="run-form">
        <label htmlFor="schedule-mode">Mode</label>
        <select
          id="schedule-mode"
          value={mode}
          onChange={(event) => setMode(event.target.value as RunMode)}
        >
          {MODES.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <label htmlFor="schedule-source">Source</label>
        <select
          id="schedule-source"
          value={source}
          onChange={(event) => setSource(event.target.value)}
        >
          <option value="">Default source</option>
          {sources.map((entry) => (
            <option key={entry.name} value={entry.name} disabled={!entry.configured}>
              {entry.name}
              {entry.configured ? "" : " (not configured)"}
            </option>
          ))}
        </select>
      </div>

      {problem && <Alert variant="warning">{problem}</Alert>}
      {saveError && <Alert variant="error">Could not save the schedule: {saveError}</Alert>}

      <div className="rules-screen__actions">
        <button
          type="button"
          className="button button--primary"
          onClick={handleSave}
          disabled={saving || problem !== null}
        >
          {saving ? "Saving…" : "Save schedule"}
        </button>
        {report && (
          <span className="rules-screen__meta">
            Next run: {report.schedule.enabled ? formatDate(report.next_run_at) : "not scheduled"}
            {report.schedule.updated_by && ` · last saved by ${report.schedule.updated_by}`}
          </span>
        )}
      </div>
    </div>
  );
}
